import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { FiArrowLeft } from 'react-icons/fi';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Container,
  Header,
  Title,
  BackButton,
  DivDetailCard,
  DetailCard,
  DetailItem,
  DetailLabel,
  DetailValue,
  DetailSection,
  UserImage,
  SectionTitle
} from './styles';
import Tooltip from '../../../components/tooltip';
import Loading from '../../../components/Loading';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  profilePicture?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  status?: string;
  permissions?: string[];
  department?: string;
  phone?: string;
  address?: string;
  nameOrganization?: string;
  profile?: string;
}

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchRef = useRef(false);

  useEffect(() => {
    const fetchUser = async () => {
      if (fetchRef.current) return;
      fetchRef.current = true;

      setIsLoading(true);
      try {
        console.log('Fetching user details for ID:', id);
        const token = localStorage.getItem('@App:token');
        if (!token) {
          console.error('No auth token found');
          return;
        }

        const response = await api.get(`/api/users/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('User data received:', response.data);
        setUser(response.data);
      } catch (error: any) {
        console.error('Error loading user:', error);
        // If there's no auth, redirect to login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate]);

  const handleBack = () => {
    navigate('/users');
  };

  return (
    <Container>
      <Header>
        <Tooltip text={t('form.user.backToUsers')}>
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>

      {isLoading ? (
        <Loading />
      ) : user ? (
        <DivDetailCard>
          <Title>{t('form.user.userDetails')}</Title>

          <DetailCard>
            {/* <UserImage 
              src={'/BG Sinergy.png'} 
              alt={user.name} 
            /> */}
            
            <DetailSection>
              <DetailItem>
                <DetailLabel>{t('form.user.name')}</DetailLabel>
                <DetailValue>{user.name}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('form.user.email')}</DetailLabel>
                <DetailValue>{user.email}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('form.user.organization')}</DetailLabel>
                <DetailValue>{user.nameOrganization || t('form.user.notSpecified')}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('form.user.role')}</DetailLabel>
                <DetailValue>{user.role}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('form.user.phone')}</DetailLabel>
                <DetailValue>{user.phone || t('form.user.notSpecified')}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>{t('form.user.profile')}</DetailLabel>
                <DetailValue>{user.profile || t('form.user.notSpecified')}</DetailValue>
              </DetailItem>
              
            </DetailSection>
            
            {user.permissions && user.permissions.length > 0 && (
              <DetailSection>
                <SectionTitle>{t('form.user.permissions')}</SectionTitle>
                {user.permissions.map((permission, index) => (
                  <DetailItem key={index}>
                    <DetailValue>{permission}</DetailValue>
                  </DetailItem>
                ))}
              </DetailSection>
            )}
          </DetailCard>
        </DivDetailCard>
      ) : (
        <div>{t('form.user.userNotFound')}</div>
      )}
    </Container>
  );
};

export default UserDetails; 